import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  canvasToPngBlob,
  isCanvasBlank,
} from '@/lib/pdf/signatureRaster';

export interface DrawSignaturePadHandle {
  clear: () => void;
  isEmpty: () => boolean;
  toPngBlob: () => Promise<Blob>;
}

interface DrawSignaturePadProps {
  color: string;
  /** 1–100 thickness percent */
  thicknessPct: number;
  onDirtyChange?: (dirty: boolean) => void;
}

const PAD_W = 560;
const PAD_H = 220;

const DrawSignaturePad = forwardRef<DrawSignaturePadHandle, DrawSignaturePadProps>(
  function DrawSignaturePad({ color, thicknessPct, onDirtyChange }, ref) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const drawing = useRef(false);
    const last = useRef<{ x: number; y: number } | null>(null);
    const [dirty, setDirty] = useState(false);

    const lineWidth = Math.max(1.5, (thicknessPct / 100) * 14);

    const markDirty = (value: boolean) => {
      setDirty(value);
      onDirtyChange?.(value);
    };

    useImperativeHandle(ref, () => ({
      clear: () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
        markDirty(false);
      },
      isEmpty: () => {
        const canvas = canvasRef.current;
        if (!canvas) return true;
        return isCanvasBlank(canvas);
      },
      toPngBlob: async () => {
        const canvas = canvasRef.current;
        if (!canvas) throw new Error('No signature pad');
        // Flatten DPR canvas into CSS-pixel size so trim/export sees real ink
        const flat = document.createElement('canvas');
        flat.width = PAD_W;
        flat.height = PAD_H;
        const ctx = flat.getContext('2d');
        if (!ctx) throw new Error('No signature pad context');
        ctx.drawImage(canvas, 0, 0, PAD_W, PAD_H);
        return canvasToPngBlob(flat);
      },
    }));

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = PAD_W * dpr;
      canvas.height = PAD_H * dpr;
      canvas.style.width = `${PAD_W}px`;
      canvas.style.height = `${PAD_H}px`;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    }, []);

    const pos = (e: ReactPointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current!;
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((e.clientX - rect.left) / rect.width) * PAD_W,
        y: ((e.clientY - rect.top) / rect.height) * PAD_H,
      };
    };

    const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      canvas.setPointerCapture(e.pointerId);
      drawing.current = true;
      last.current = pos(e);
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.lineTo(last.current.x + 0.01, last.current.y + 0.01);
      ctx.stroke();
      markDirty(true);
    };

    const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
      if (!drawing.current || !last.current) return;
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      const p = pos(e);
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      last.current = p;
    };

    const onPointerUp = () => {
      drawing.current = false;
      last.current = null;
    };

    return (
      <canvas
        ref={canvasRef}
        className="pdfe-sig-pad-canvas"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        aria-label="Draw signature"
        data-dirty={dirty ? '1' : '0'}
      />
    );
  }
);

export default DrawSignaturePad;
